import { FetchResult } from "@apollo/client"
import { useCallback, useEffect, useMemo, useState } from "react"
import { usePreviousDistinct } from "react-use"
import { useQueryParam } from "use-query-params"
import {
  ResetCompletionFeedbackMutation,
  SaveCompletionFeedbackMutation
} from "../../api/generated"
import { usePreMountLocation } from "../../utils/usePreviousLocation"
import { CompletionFilterKey } from "../context/completion-filters"
import useReviewNotify from "../context/review/useReviewNotify"
import { UseReviewableCompletionsReturn } from "./ReviewContextProvider"
import {
  TreeNode,
  TreeNodeAny,
  TreeNodeParentNode,
  isLeaf
} from "./tree/path-tree"
import useCompletionsStore, {
  UseCompletionsStoreReturn
} from "./useCompletionsStore"
import useReviewMutations, { ReviewItemOptions } from "./useReviewMutations"

const getRootPath = (store: UseCompletionsStoreReturn) => {
  let root = store.tree
  // if there's only one root child, select it instead
  if (root?.children.size === 1) {
    root = root.children.values().next().value
  }
  return root
}

type SelectedParamKey = "ui_selected" | "ui_selected_parent"

// The param keys "ui_selected" and "ui_selected_parent" correspond to different node types.
// "ui_selected" is always a "completion" node, while "ui_selected_parent" is always a parent node
// (i.e., not a "completion" node).
type TreeNodeByParamKey<T extends SelectedParamKey> = T extends "ui_selected"
  ? TreeNode<"completion">
  : TreeNodeParentNode

// Manages selected nodes based on URL query parameters. Converts between number IDs to string IDs.
// These string IDs allow for better scoping of individual nodes in the tree structure, since you may
// have multiple nodes with the same source ID (like if there were multiple lessons or steps across cohorts).
function useSelectedNode<T extends SelectedParamKey>({
  store,
  paramKey
}: {
  store: UseCompletionsStoreReturn
  paramKey: T
}) {
  // Remove the tree root ID prefix when setting the selected node ID since
  // our tree is derooted (the root node is implicit). This keeps the URL param cleaner.
  function encodeSelectNodeId(id: string | null | undefined) {
    if (id && typeof id === "string") {
      const derootedId = id.split("-").slice(1).join("-")
      // Fallback to the root ID if the derooted ID is empty.
      return derootedId || id
    }
    return id
  }

  // Transform the selectedNodeId from the URL param into the internal tree node ID format.
  // This involves adding the tree root ID prefix to the ID.
  function decodeSelectedNodeId(
    id: string | null | undefined | (string | null)[]
  ) {
    if (id && !Array.isArray(id)) {
      // Handle case where id is a number (legacy support).
      if (!isNaN(Number(id))) {
        // Map the legacy numeric ID to the corresponding tree node ID where the
        // tree node's final segment matches the numeric ID. The legacy and newer
        // ids follow the same pattern in that "completion" types use the path item's
        // ID while the "step" and "lesson" types use the path item item's source ID.
        const item = [...(store?.map?.values() || [])].find(
          (item) => id === item.id.split("-").pop()?.split(":").pop()
        )

        if (item) return item.id
      }

      // If the ID already has the root prefix, just return it as is.
      if (id.startsWith(store?.tree?.id || "")) {
        return id
      }

      return `${store?.tree?.id}-${id}`
    }
    return id?.toString() || null
  }

  const [selectedNodeId, setSelectedNodeId] = useQueryParam(paramKey, {
    encode: encodeSelectNodeId,
    decode: decodeSelectedNodeId
  })

  const map = store.map || new Map<string, TreeNodeAny>()
  const selectedNode = selectedNodeId ? map.get(selectedNodeId) : null

  // Type guard to ensure the selected node matches the expected type based on paramKey.
  function nodeTypeFilter(
    node?: TreeNodeAny | null
  ): node is TreeNodeByParamKey<T> {
    if (node) {
      switch (paramKey) {
        case "ui_selected":
          return node?.type === "completion"
        case "ui_selected_parent":
          return node?.type !== "completion"
      }
    }

    return false
  }

  const selected = nodeTypeFilter(selectedNode) ? selectedNode : null

  // In the event that the selectedId exists and there are items in the map yet we
  // have no selected item (the selectedId isn't keyed in the map) then fall back to
  // finding the item by matching the completion id to the selectedId query param.
  useEffect(() => {
    if (selectedNodeId && !selected && map.size) {
      const node = [...map.values()].find(
        (node) =>
          node.data?.id === parseInt(selectedNodeId) && nodeTypeFilter(node)
      )
      if (node) {
        setSelectedNodeId(node.id)
      }
    }
  }, [map.size, selectedNodeId, selected])

  return [selected, setSelectedNodeId] as const // return as const to preserve tuple type
}

export type UseReviewContextReturn = ReturnType<typeof useReviewContext>

export const useReviewContext = (context: UseReviewableCompletionsReturn) => {
  const store = useCompletionsStore(context)
  const mutations = useReviewMutations()
  const preMountLocation = usePreMountLocation()

  const [selected, setSelectedId] = useSelectedNode({
    store,
    paramKey: "ui_selected"
  })
  const [selectedParent, setSelectedParentId] = useSelectedNode({
    store,
    paramKey: "ui_selected_parent"
  })
  const selectedId = selected?.id
  const selectedParentId = selectedParent?.id
  const prevSelectedParentId = usePreviousDistinct(selectedParentId)

  // Track if any review status updates are made to inbox items.
  // These types of changes affect which itmes are listed in the Inbox list view
  // depending on the filter settings.
  const [isPendingSyncReviewUpdates, setIsPendingSyncReviewUpdates] =
    useState(false)
  const handleIsPendingSyncReviewUpdates = useCallback(() => {
    if (!isPendingSyncReviewUpdates) setIsPendingSyncReviewUpdates(true)
  }, [isPendingSyncReviewUpdates])

  // This indicates the user is returning to the list view after having potentially
  // updated inbox items in a way that would effectively remove them from the current
  // list of inbox items. Use a callback to respond.
  const prevSelectedId = usePreviousDistinct(selectedId)
  const useSyncReviewUpdates = (callback?: () => void) => {
    useEffect(() => {
      if (!selectedId && prevSelectedId && isPendingSyncReviewUpdates) {
        callback?.()
        setIsPendingSyncReviewUpdates(false)
      }
    }, [selectedId, callback])
  }

  // Conditionally refetch inbox items in order to sync any review status updates
  // that would require syncing the Inbox list view with those updates.
  useSyncReviewUpdates(context.refetch)

  let isLoaded = !context.loading

  let root: TreeNodeParentNode | null = useMemo(
    () => (store.tree ? getRootPath(store) : null),
    [store.tree]
  )

  const returnToRoot = () => {
    setSelectedId(root?.id)
  }

  // if the selectedParent is a root node and there's a better root, select it
  useEffect(() => {
    if (
      root &&
      !selectedParent &&
      // Safegaurd against resetting to same parent since
      // this indicates we are leaving inbox.
      root.id !== prevSelectedParentId
    ) {
      setSelectedParentId(root.id, "replaceIn")
    }
  }, [selectedParent, root])

  useEffect(() => {
    if (selectedParent && root) {
      if (root.type === "path" && selectedParent.type === "root") {
        setSelectedParentId(root.id, "replaceIn")
      }
    }
  }, [selectedParentId, root, selectedParent])

  // ACTIONS: select + navigation
  const select = (item: TreeNodeAny) => {
    if (item === null) {
      returnToRoot()
    } else {
      if (isLeaf(item)) {
        setSelectedId(item.id)
      } else {
        setSelectedId(null)
        setSelectedParentId(item.id)
      }
    }
  }

  const updateNodeCompletion =
    (item: TreeNode<"completion">) =>
    (
      result:
        | FetchResult<SaveCompletionFeedbackMutation>
        | FetchResult<ResetCompletionFeedbackMutation>
    ) => {
      if (result.data) {
        let data =
          "saveCompletionFeedback" in result.data
            ? result.data.saveCompletionFeedback
            : "resetCompletionFeedback" in result.data
            ? result.data.resetCompletionFeedback
            : undefined
        if (data) {
          const node = { ...item }
          node.data.completion = data
          store.actions.set(node.id, node)
        }
      }
    }

  const [reviewNotify, setReviewNotify] = useReviewNotify()
  const reviewItem = async ({
    item,
    userpointsExplanation,
    userpointsEarned,
    notify = reviewNotify
  }: ReviewItemOptions) => {
    const result = await mutations.reviewItem({
      item,
      userpointsExplanation,
      userpointsEarned,
      notify
    })
    updateNodeCompletion(item)(result)
    handleIsPendingSyncReviewUpdates()
    setReviewNotify(notify)
    return result
  }

  const reviewSelected = async ({
    userpointsExplanation,
    userpointsEarned,
    notify
  }: Omit<ReviewItemOptions, "item">) => {
    if (selected && selected.type === "completion") {
      return reviewItem({
        item: selected,
        userpointsExplanation,
        userpointsEarned,
        notify
      })
    }
  }

  // quick action for menu
  const markReviewed = async (item: TreeNode<"completion">) => {
    return reviewItem({
      item,
      userpointsExplanation: "",
      userpointsEarned: 0,
      notify: false
    })
  }

  const reviewBatch = async ({
    items,
    userpointsExplanation,
    userpointsEarned,
    notify
  }: Omit<ReviewItemOptions, "item"> & {
    items: ReviewItemOptions["item"][]
  }) => {
    let results = []
    // loop through items and call reviewItem
    for (let item of items) {
      // todo apply userpoints_earned as a percentile score of item.userpoints_available
      let result = await reviewItem({
        item,
        userpointsExplanation,
        userpointsEarned,
        notify
      })
      results.push(result)
    }
    return results
  }

  const resetSelectedReview = async () => {
    if (selected && selected.type === "completion") {
      let result = await mutations.resetReview(selected)
      updateNodeCompletion(selected)(result)
      handleIsPendingSyncReviewUpdates()
    }
  }

  const filterContext = (key: CompletionFilterKey, order?: "asc" | "desc") => {
    returnToRoot()
    context.filter(key, order)
  }

  const selectRoot = () => {
    setSelectedParentId(root?.id, "replaceIn")
  }

  const clearAllSelections = () => {
    context.backToParentContext()
    returnToRoot()
    setSelectedParentId(null)
  }

  const actions = {
    select,
    returnToRoot,
    clearAllSelections,
    reviewSelected,
    reviewItem,
    markReviewed,
    resetSelectedReview,
    reviewBatch,
    filterContext,
    selectRoot,
    getItem: (itemId: string) => store.actions.get(itemId),
    useSyncReviewUpdates
  }

  let state = {
    selectedParent,
    selected,
    actions,
    isLoaded,
    context,
    root,
    filter: context.selectedFilter,
    filters: context.filters,
    hasParentContext: context.hasParentContext(),
    backToParentContext: context.backToParentContext,
    preMountLocation
  }

  // debug stuff
  // window.reviewContext = {
  //   getState: () => state,
  //   actions
  // }

  return state
}
